import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { RefreshCw, ChevronDown, ChevronRight, Anchor, Info, BookOpen } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const GLOSSARY = [
  ["Funds", "Breadth: how many distinct funds opened a NEW position in the stock during the quarter (all 13F filers, not just elite). TSLA 77 = 77 funds initiated. Breadth is a core model input — a confirmed run matters most when many funds bought it (run-up × breadth is the strongest term)."],
  ["Estimated basis", "The cohort's reconstructed entry price: average daily close over the 13F build quarter (the quarter in which the funds accumulated). 13F filings don't disclose entry prices — this is our proxy for what the cohort paid."],
  ["BP_rel (basis premium)", "How far the stock has moved from the cohort's estimated basis, NET of its sector's move over the same window. TSLA −19.7% = trading 19.7% below the buyers' estimated cost, sector-adjusted — the market has so far disagreed with the cohort. Positive BP_rel = the market confirmed the buy."],
  ["BP dec", "The BP_rel decile within the quarter's cross-section. D1 = most underwater vs buyers' basis, D10 = biggest confirmed run-up. Our 495k-event study: higher deciles outperform — the run-up is confirmation, not exhaustion (D1 names like TSLA score poorly for a reason)."],
  ["Score (S1–S10)", "Decile of the V3 expected-alpha score within the quarter. S10 = top 10% of expected 126-day sector & cap-adjusted alpha; S1 = bottom. Out-of-sample, S10-minus-S1 spread was +16.4%/126d (t 5.8). The S6–S9 band is the validated 'green light' zone used by the Earnings Alpha engine."],
  ["10+ buyers", "Breadth amplifier: at least 10 distinct new buyers this quarter. Historically amplifies confirmed run-ups."],
  ["hiC centrality", "The stock sits in the sweet spot of the elite co-holding network — held by top-5% expected-alpha funds, but not at the crowded extreme. Centrality measures collective elite ownership, not one fund's skill."],
  ["⚠ crowded topC", "Top-decile centrality: the stock is one of the MOST co-owned names among elite funds. At high run-ups this crowding historically reduces the forward edge (saturation) — the model penalizes it via the centrality × BP interaction."],
  ["dip lottery", "Deep-underwater name (BP decile 1-3) ranked up by a street target more than 2× the price — usually analysts slow to cut after a crash (e.g., ADCT). Historically a REAL mean (+13%) but a lottery shape: median ≈ +0.8% and 28% of these lose >20% in 126 sessions. The Anchor Long 10 sleeve excludes them by rule; treat top-ranked dip-lottery rows as venture bets, not core signals."],
  ["trend alive / fading", "The stock's last 21 sessions vs its sector. 'Trend alive' = still outperforming into today (validated fast layer: +21.9% vs +1.3% for faders in the confirmation zone); 'fading' = momentum gone. Re-marked daily — the only mid-holding signal that survived testing."],
  ["Tgt upside", "Street consensus remaining upside: median sell-side price target (≤30 days old) vs current price. TSLA +14% = analysts' median target sits 14% above spot. Forward-looking entry input (coef +6.9%, t 4.8); NOT an exit signal — tested and rejected for exits."],
  ["Exp α 126d / 63d", "The model's expected alpha over the next 126 / 63 trading sessions versus sector & cap-matched peers, from the V3 regression. Historical estimates, gross of costs, right-skewed — TSLA −7.4% means the model expects underperformance vs its peer group."],
  ["Captured α", "Realized sector-relative alpha from the reveal date to today — what the signal has actually delivered so far this season. TSLA +27.0% has already outrun its sector since disclosure despite the low score (scores are formed at reveal; captured α is live tracking)."],
  ["Day (47/126)", "Trading sessions elapsed since THAT stock's reveal date, out of the 126-session evaluation window the model was fitted on. Clocks differ per stock: funds file 13Fs at different times across the ~6-week season, and each cohort's clock starts when its own buyers' filings became public — so an early-filed cohort can show 60/126 while a late one shows 47/126. Completed quarters show 126/126."],
  ["Reveal date", "The first date the cohort's buying became publicly observable (earliest 13F filing date). All scores, ranks and forward windows are anchored here — never on information before it was public."],
];

const AMP_STYLE = {
  "10+ buyers": "border-violet-500/50 text-violet-400",
  "hiC centrality": "border-emerald-500/50 text-emerald-400",
  "crowded topC": "border-amber-500/60 text-amber-400",
  "trend alive": "border-sky-500/50 text-sky-400",
  "fading": "border-zinc-500/40 text-zinc-500",
  "dip lottery": "border-rose-500/60 text-rose-400",
};

const scoreBadge = (sd) => {
  if (sd >= 10) return "bg-emerald-600 text-white";
  if (sd >= 9) return "bg-emerald-700/80 text-white";
  if (sd >= 8) return "bg-sky-700 text-white";
  return "bg-zinc-600 text-zinc-100";
};

export const EntryAnchorTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [quarters, setQuarters] = useState([]);
  const [quarter, setQuarter] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [showGloss, setShowGloss] = useState(false);
  const pollRef = useRef(null);
  const debRef = useRef(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const fetchLive = useCallback(async (q, opts = {}) => {
    const s = opts.search !== undefined ? opts.search : "";
    const all = opts.showAll !== undefined ? opts.showAll : false;
    const params = new URLSearchParams();
    if (q) params.set("quarter", q);
    params.set("limit", all || s ? "0" : "200");
    if (s) params.set("search", s);
    const res = await fetch(`${API}/entry-anchor/live?${params}`, auth);
    if (!res.ok) return;
    const d = await res.json();
    setData(d);
    if (!q && d.quarter) setQuarter(d.quarter);
    if (d.status === "running") {
      clearTimeout(pollRef.current);
      pollRef.current = setTimeout(() => fetchLive(q || d.quarter, opts), 8000);
    }
  }, [token]);

  useEffect(() => {
    fetch(`${API}/entry-anchor/quarters`, auth)
      .then((r) => r.json())
      .then((d) => setQuarters(d.quarters || []));
    fetchLive();
    return () => clearTimeout(pollRef.current);
  }, [fetchLive]);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/entry-anchor/refresh?quarter=${quarter}`, { method: "POST", ...auth });
      if (res.status === 409) toast.info("Computation already running");
      else toast.success("Computation started (~1-2 min)");
      setTimeout(() => fetchLive(quarter), 5000);
    } finally {
      setLoading(false);
    }
  };

  const rows = data?.rows || [];
  const hist = data?.histogram || [];
  const maxHist = Math.max(...hist.map((h) => h.n_events), 1);

  return (
    <div className="space-y-6" data-testid="entry-anchor-tab">
      {/* Methodology explainer */}
      <Card className="border-primary/30">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Anchor className="w-4 h-4" /> Entry Anchoring — expected forward alpha model
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            For every new 13F buy we reconstruct the funds' <b>estimated entry price</b> (average price during the
            accumulation quarter) and measure <b>BP_rel</b>: how far the stock has run from that basis <b>net of
            its sector</b>. Our 495k-event study found the run-up is <b>confirmation, not exhaustion</b> — and that
            it compounds with how <b>broadly</b> the stock was bought and whether the move is still alive. ETFs excluded.
          </p>
          <p>
            <b>The score is a regression</b> (quarterly Fama-MacBeth, 2022-11+, no fund-skill inputs): expected 126-day
            alpha vs sector &amp; cap-matched peers from <b>BP_rel rank</b>, <b>breadth</b> (number of new buyers),
            <b> centrality</b> (position in the top-5% funds' co-holding network — including a <b>crowding
            interaction</b>: at high run-ups, heavy elite co-ownership reduces the expected edge),
            <b> 21-session sector-relative trend</b>, and the key interaction <b>run-up × breadth</b> — the
            single strongest term: a confirmed run matters most when many funds bought it.
            <b> Out-of-sample</b> (fitted through 2024, predicting 2025-26): top-vs-bottom score decile spread
            <b> +16.4%/126d (t 5.8)</b>, top-decile median +5.9%. Skill (ia_skew) terms were tested head-to-head
            and rejected by the out-of-sample race; our quarterly Kensys targets were also tested and added
            nothing once street consensus is in.
          </p>
          <p>
            <b>Remaining upside</b>: the model also uses the <b>street consensus price target</b> (daily series,
            ≤30d stale) as a forward-looking anchor — "Tgt upside" = median analyst target vs current price,
            ranked within the quarter. Conditional on a confirmed run-up, names with more remaining consensus
            upside kept outperforming (coef +6.9%, t 4.8) — confirmation (BP) tells you the market agrees,
            upside tells you how much room the thesis still has.
          </p>
          <p>
            <b>Badges</b> explain each name's drivers: <b>10+ buyers</b> (breadth) · <b>hiC centrality</b> (elite
            co-ownership sweet spot) · <b>⚠ crowded topC</b> (top-decile centrality names historically fade) ·
            <b> trend alive / fading</b> (last 21 sessions vs sector). <b>Cadence:</b> signals are quarterly (13F
            seasons, ~45d after quarter end); scores re-marked daily at 22:15 UTC against current prices &amp; trend.
            Expected alphas are historical model estimates, gross of costs, right-skewed — not guarantees.
          </p>
        </CardContent>
      </Card>

      {/* Glossary */}
      <Card>
        <CardHeader className="py-3 cursor-pointer select-none" data-testid="entry-anchor-glossary-toggle"
                    onClick={() => setShowGloss(!showGloss)}>
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            {showGloss ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <BookOpen className="w-4 h-4" /> Glossary — every column &amp; badge explained
          </CardTitle>
        </CardHeader>
        {showGloss && (
          <CardContent className="pt-0" data-testid="entry-anchor-glossary">
            <div className="grid gap-x-8 gap-y-3 md:grid-cols-2">
              {GLOSSARY.map(([term, def]) => (
                <div key={term} className="text-sm">
                  <span className="font-semibold text-foreground">{term}</span>
                  <span className="text-muted-foreground"> — {def}</span>
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <select
          data-testid="entry-anchor-quarter-select"
          className="h-9 rounded-md border bg-background px-3 text-sm"
          value={quarter}
          onChange={(e) => { setQuarter(e.target.value); fetchLive(e.target.value); }}
        >
          {quarters.map((q) => (
            <option key={q.quarter} value={q.quarter}>{q.quarter} ({q.n_events.toLocaleString()} events)</option>
          ))}
        </select>
        <Button size="sm" onClick={refresh} disabled={loading || data?.status === "running"}
                data-testid="entry-anchor-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${data?.status === "running" ? "animate-spin" : ""}`} />
          {data?.status === "running" ? "Computing…" : "Refresh"}
        </Button>
        {data?.computed_at && (
          <span className="text-xs text-muted-foreground">
            computed {new Date(data.computed_at).toLocaleString()} · prices through {data?.meta?.price_data_through}
            {data?.meta?.centrality_snapshot && <> · centrality snapshot {data.meta.centrality_snapshot}</>}
          </span>
        )}
      </div>

      {data?.status === "missing" && (
        <Card><CardContent className="py-8 text-center text-muted-foreground" data-testid="entry-anchor-empty">
          No scores computed yet for {data.quarter}. Hit Refresh to run the first computation.
        </CardContent></Card>
      )}
      {data?.status === "error" && (
        <Card><CardContent className="py-6 text-sm text-red-500">Compute failed: {data.error}</CardContent></Card>
      )}

      {data?.status === "ready" && (
        <>
          {/* Decile histogram */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">BP_rel decile distribution — {data.quarter}</CardTitle>
              <CardDescription className="text-xs">
                {data.meta.n_events_scored.toLocaleString()} scored events across {data.meta.n_cohorts?.toLocaleString()} stocks
                (of {data.meta.n_events_total.toLocaleString()} events; rest = ETFs / no price coverage).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-1 h-24" data-testid="entry-anchor-histogram">
                {hist.map((h) => (
                  <div key={h.decile} className="flex-1 flex flex-col items-center gap-1">
                    <div
                      className={`w-full rounded-t ${h.decile >= 8 ? "bg-emerald-500" : "bg-zinc-500/40"}`}
                      style={{ height: `${Math.max(4, (h.n_events / maxHist) * 80)}px` }}
                      title={`D${h.decile}: ${h.n_events} events`}
                    />
                    <span className="text-[10px] text-muted-foreground">D{h.decile}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Signal table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center justify-between gap-3 flex-wrap">
                <span>
                  Model-ranked signals — {search ? `${rows.length} match${rows.length === 1 ? "" : "es"}` : showAll ? `all ${rows.length}` : `top ${rows.length}`} of {(data.n_rows_total || data.meta.n_cohorts)?.toLocaleString()} scored stocks
                </span>
                <span className="flex items-center gap-2">
                  <input
                    type="text"
                    value={search}
                    placeholder="Search symbol…"
                    data-testid="entry-anchor-search"
                    onChange={(e) => {
                      const v = e.target.value;
                      setSearch(v);
                      clearTimeout(debRef.current);
                      debRef.current = setTimeout(() => fetchLive(quarter, { search: v, showAll }), 350);
                    }}
                    className="h-7 w-36 rounded-md border border-border bg-background px-2 text-xs font-normal focus:outline-none focus:ring-1 focus:ring-emerald-600"
                  />
                  <button
                    data-testid="entry-anchor-show-all"
                    onClick={() => {
                      const next = !showAll;
                      setShowAll(next);
                      fetchLive(quarter, { search, showAll: next });
                    }}
                    className="h-7 rounded-md border border-border px-2 text-xs font-normal text-muted-foreground hover:bg-muted/50"
                  >
                    {showAll ? "Top 200" : "Show all"}
                  </button>
                </span>
              </CardTitle>
              <CardDescription className="text-xs flex items-center gap-1">
                <Info className="w-3 h-3" />
                Exp α = regression estimate of 126d / 63d alpha vs sector &amp; cap peers · S-dec = score decile
                within the quarter · no fund-skill inputs
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="entry-anchor-table">
                <thead>
                  <tr className="text-xs text-muted-foreground border-b">
                    <th className="text-left py-2 pr-2"></th>
                    <th className="text-left py-2 pr-3">Symbol</th>
                    <th className="text-left pr-3">Sector</th>
                    <th className="text-left pr-3">Cap</th>
                    <th className="text-right pr-3">Funds</th>
                    <th className="text-right pr-3">BP_rel</th>
                    <th className="text-right pr-3">BP dec</th>
                    <th className="text-center pr-3">Score</th>
                    <th className="text-left pr-3">Drivers</th>
                    <th className="text-right pr-3">Tgt upside</th>
                    <th className="text-right pr-3">Exp α 126d</th>
                    <th className="text-right pr-3">Exp α 63d</th>
                    <th className="text-right pr-3">Captured α</th>
                    <th className="text-right pr-3">Day</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <>
                      <tr key={r.symbol} className="border-b border-border/40 hover:bg-muted/30 cursor-pointer"
                          onClick={() => setExpanded(expanded === r.symbol ? null : r.symbol)}
                          data-testid={`entry-anchor-row-${r.symbol}`}>
                        <td className="py-2 pr-1">
                          {expanded === r.symbol ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        </td>
                        <td className="py-2 pr-3 font-semibold">{r.symbol}</td>
                        <td className="pr-3 text-xs text-muted-foreground">{r.sector}</td>
                        <td className="pr-3 text-xs">{r.cap_bucket || "—"}</td>
                        <td className="pr-3 text-right">{r.n_funds}</td>
                        <td className={`pr-3 text-right font-mono ${r.bp_rel >= 0 ? "text-emerald-500" : "text-red-400"}`}>
                          {(r.bp_rel * 100).toFixed(1)}%
                        </td>
                        <td className="pr-3 text-right text-xs text-muted-foreground">D{r.decile}</td>
                        <td className="pr-3 text-center">
                          <Badge className={`${scoreBadge(r.score_decile)} text-[10px]`}>S{r.score_decile}</Badge>
                        </td>
                        <td className="pr-3">
                          <div className="flex flex-wrap gap-1">
                            {(r.amplifiers || []).map((a) => (
                              <span key={a}
                                    className={`text-[9px] border rounded-full px-1.5 py-0.5 whitespace-nowrap ${AMP_STYLE[a] || "border-zinc-500/40 text-zinc-400"}`}>
                                {a === "crowded topC" ? "⚠ " : ""}{a}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="pr-3 text-right font-mono text-xs">
                          {r.target_upside != null ? `${r.target_upside >= 0 ? "+" : ""}${r.target_upside.toFixed(0)}%` : "—"}
                        </td>
                        <td className="pr-3 text-right font-mono text-emerald-500">
                          {r.exp_alpha_126 >= 0 ? "+" : ""}{r.exp_alpha_126.toFixed(1)}%
                        </td>
                        <td className="pr-3 text-right font-mono text-xs text-emerald-600/80">
                          {r.exp_alpha_63 >= 0 ? "+" : ""}{r.exp_alpha_63.toFixed(1)}%
                        </td>
                        <td className={`pr-3 text-right font-mono ${(r.alpha_captured ?? 0) >= 0 ? "text-emerald-500" : "text-red-400"}`}>
                          {r.alpha_captured != null ? `${r.alpha_captured >= 0 ? "+" : ""}${r.alpha_captured.toFixed(1)}%` : "—"}
                        </td>
                        <td className="pr-3 text-right text-xs text-muted-foreground">
                          {Math.min(r.days_elapsed, r.window)}/{r.window}
                        </td>
                      </tr>
                      {expanded === r.symbol && (
                        <tr key={`${r.symbol}-x`} className="bg-muted/20">
                          <td colSpan={14} className="py-2 px-6">
                            <div className="text-xs text-muted-foreground mb-1">
                              Revealed {r.reveal_date} · BP decile {r.decile} (rank {Math.round((r.bp_pct || 0) * 100)})
                              {r.cen_pct != null && <> · centrality pct {Math.round(r.cen_pct * 100)} ({r.cen_band})</>}
                              {r.trend_21 != null && <> · 21-session trend vs sector {r.trend_21 >= 0 ? "+" : ""}{r.trend_21.toFixed(1)}%</>}
                              {" · buyers:"}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {r.funds.map((f, i) => (
                                <span key={i} className="text-xs bg-background border rounded px-2 py-0.5">
                                  {f.name}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
              {rows.length === 0 && (
                <div className="py-6 text-center text-muted-foreground text-sm">
                  No scored stocks this quarter.
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default EntryAnchorTab;
